import type { ClientSummary } from '@/domain/client';

export type ClientStatusFilter = 'active' | 'archived' | 'all';

/** Where a client's licenses stand, by license type. */
export type LicenseHealth = 'lapsed' | 'expiring' | 'active' | 'none';

/** An end date from this year on is how the licenses of clients with no end are stored. */
const OPEN_ENDED_FROM_YEAR = 2100;

export function isOpenEnded(expiry: Date): boolean {
    return expiry.getFullYear() >= OPEN_ENDED_FROM_YEAR;
}

/** The worst of what the client's license types are doing. */
export function licenseHealth(client: ClientSummary): LicenseHealth {
    if (client.lapsedLicenses.length > 0) return 'lapsed';
    if (client.expiringLicenses.length > 0) return 'expiring';
    if (client.activeLicenses.length > 0) return 'active';
    return 'none';
}

/** An active client with a stale bioscope, or a license type that lapsed or is about to. */
export function needsAttention(client: ClientSummary): boolean {
    if (client.archived) return false;
    const license = licenseHealth(client);
    return client.staleBioscopeCount > 0 || license === 'lapsed' || license === 'expiring';
}

export function filterByStatus(
    clients: ClientSummary[],
    status: ClientStatusFilter
): ClientSummary[] {
    if (status === 'all') return clients;
    const archived = status === 'archived';
    return clients.filter((client) => client.archived === archived);
}

/** There is no internal flag on a client; the company's own accounts are told by name. */
const INTERNAL_CLIENT_NAMES = ['optoscale', 'inission', 'deltax'];

export function isInternalClient(client: ClientSummary): boolean {
    return INTERNAL_CLIENT_NAMES.includes(client.name.trim().toLowerCase());
}

/**
 * The order the table opens in: customers before the internal client, then the most bioscopes,
 * then the most recent measurement. A client that never measured goes last.
 */
export function compareClientsByDefault(a: ClientSummary, b: ClientSummary): number {
    const internal = Number(isInternalClient(a)) - Number(isInternalClient(b));
    if (internal !== 0) return internal;
    if (a.bioscopeCount !== b.bioscopeCount) return b.bioscopeCount - a.bioscopeCount;
    const measuredA = a.lastMeasurementAt?.getTime() ?? Number.NEGATIVE_INFINITY;
    const measuredB = b.lastMeasurementAt?.getTime() ?? Number.NEGATIVE_INFINITY;
    if (measuredA !== measuredB) return measuredB > measuredA ? 1 : -1;
    return a.name.localeCompare(b.name);
}
